import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import type { FormatOptionLabelMeta } from "react-select";

import { StopType } from "types/MultiStop";
import type { SelectOption } from "components/v2/Select/types";
import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { getStopPortLabel } from "@RequestFormV2/components/ReviewPage/sections/ReviewSchedule/reviewScheduleFormatters";

type StopOptionDetail = {
  title: string;
  locationName?: string;
  addressLine?: string;
};

/** Airport/port name when applicable; otherwise the address name. */
export function getStopLocationLabel(stop: ScheduleStopValue): string | undefined {
  const portLabel = getStopPortLabel(stop);
  return portLabel || stop.address_name?.trim() || undefined;
}

/** Mirrors V1 StopOptionLabel address line: "{street}, {country} {zipcode}". */
function buildStopAddressLine(stop: ScheduleStopValue): string {
  const street = stop.street_address?.trim() ?? "";
  const country = stop.country_name?.trim() ?? "";
  const zipcode = stop.zipcode?.trim() ?? "";
  const tail = [country, zipcode].filter(Boolean).join(" ");
  const separator = street && tail ? ", " : "";
  return `${street}${separator}${tail}`.trim();
}

/** Omits the address line when it would repeat the port/location label above it. */
function getStopOptionAddressLine(stop: ScheduleStopValue, locationName?: string): string | undefined {
  const line = buildStopAddressLine(stop);
  if (!line) return undefined;
  if (!locationName || line === locationName) return undefined;

  const street = stop.street_address?.trim();
  if (street && locationName.includes(street) && line.startsWith(street)) {
    return undefined;
  }

  return line;
}

/**
 * Builds pickup/delivery stop options for the multi-stop packing list dropdowns.
 * Options are filtered by stop type; `formatStopOption` renders a rich multi-line
 * label in the menu (Stop #N + location name + street/country/zipcode) and a compact
 * "Stop #N - {location}" title for the selected value (airport/port name when applicable).
 */
export function useStopSelectOptions() {
  const intl = useIntl();
  const stops = useWatch({ name: "schedule.stops" }) as ScheduleStopValue[] | undefined;

  return useMemo(() => {
    const allStops = stops ?? [];
    const isMultiStop = allStops.length > 2;

    const detailByKey = new Map<string, StopOptionDetail>();
    const pickupOptions: SelectOption[] = [];
    const deliveryOptions: SelectOption[] = [];

    allStops.forEach((stop, index) => {
      if (!stop?.stop_key) return;

      const title = intl.formatMessage(
        { id: "request-form.schedule.stopNumber", defaultMessage: "Stop #{number}" },
        { number: index + 1 },
      );
      const locationName = getStopLocationLabel(stop);
      const addressLine = getStopOptionAddressLine(stop, locationName);
      detailByKey.set(stop.stop_key, { title, locationName, addressLine });

      const option: SelectOption = { label: title, value: stop.stop_key };
      if (stop.stop_type === StopType.PICKUP) {
        pickupOptions.push(option);
      } else if (stop.stop_type === StopType.DROPOFF) {
        deliveryOptions.push(option);
      }
    });

    const formatStopOption = (option: SelectOption, meta: FormatOptionLabelMeta<SelectOption>) => {
      const detail = detailByKey.get(option.value);
      if (!detail) return option.label;
      if (meta.context === "value") {
        return detail.locationName ? `${detail.title} - ${detail.locationName}` : detail.title;
      }

      return (
        <>
          <span className="cb-select__option-primary">{detail.title}</span>
          {detail.locationName && <span className="cb-select__option-supporting-text">{detail.locationName}</span>}
          {detail.addressLine && <span className="cb-select__option-supporting-text">{detail.addressLine}</span>}
        </>
      );
    };

    return { isMultiStop, pickupOptions, deliveryOptions, formatStopOption };
  }, [stops, intl]);
}
